import apiClient from "./axios";
import { ApiResponse, PaginatedResponse } from "../types";

const API_BASE =
  process.env.REACT_APP_API_URL || "https://api.payiqapp.com/api/v1";
const SUPPORT_WS_URL =
  process.env.REACT_APP_SUPPORT_WS_URL ||
  API_BASE.replace(/^http/, "ws").replace(/\/api\/v1$/, "/support/ws");

export type SupportConversationStatus = "open" | "pending" | "resolved" | "closed";
export type SupportConversationPriority = "low" | "normal" | "high" | "urgent";
export type SupportMessageSenderType = "user" | "admin" | "system" | "ai";
export type SupportMessageChannel = "in_app" | "email" | "system";
export type SupportWorkflowType =
  | "account_access_security"
  | "payments_transfers"
  | "cards"
  | "kyc_verification"
  | "orders_delivery"
  | "technical_issue"
  | "general_support";
export type SupportWorkflowStatus =
  | "not_started"
  | "active"
  | "waiting_on_user"
  | "escalated"
  | "resolved";

export interface SupportWorkflowChecklistItem {
  id: string;
  label: string;
  checked: boolean;
}

export interface SupportWorkflowHistoryEntry {
  at: string;
  adminId: string | null;
  action: string;
  note?: string;
}

export interface SupportWorkflowPreset {
  type: SupportWorkflowType;
  label: string;
  description: string;
  entityTypes: string[];
  checklist: Array<{ id: string; label: string }>;
}

export interface SupportConversation {
  id: string;
  category: string | null;
  subject: string | null;
  status: SupportConversationStatus;
  priority: SupportConversationPriority;
  assignedAdminId: string | null;
  assignedAdminName: string | null;
  assignedAdminProfilePhoto: string | null;
  triageCategory: string | null;
  triageStatus: "suggested" | "fallback" | null;
  triageLastMessageId: string | null;
  triageConfidence: number | null;
  triageSummary: string | null;
  aiSuggestedReply: string | null;
  requiresHumanSupport: boolean;
  workflowType: SupportWorkflowType | null;
  workflowStatus: SupportWorkflowStatus;
  workflowChecklist: SupportWorkflowChecklistItem[];
  workflowLinkedEntityType: string | null;
  workflowLinkedEntityId: string | null;
  workflowHistory: SupportWorkflowHistoryEntry[];
  suggestedWorkflowType: SupportWorkflowType;
  workflowContext: {
    userTier: string;
    userStatus: string;
    isPhoneVerified: boolean;
    isEmailVerified: boolean;
    faceVerificationStatus: string;
    addressDocumentStatus: string;
    hasAccountNumber: boolean;
    quickLinks: Array<{ label: string; url: string }>;
  } | null;
  user: {
    id: string;
    name: string;
    email: string | null;
    phoneNumber: string | null;
  } | null;
  /**
   * Set instead of `user` when a business asked.
   *
   * The two are mutually exclusive — the database enforces exactly one owner
   * — so the console switches on whichever is present rather than guessing.
   * `member` is the person who opened it, and goes null if they later leave
   * the company while the thread stays with the business.
   */
  business: {
    id: string;
    name: string;
    member: { id: string; name: string } | null;
  } | null;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessage {
  id: string;
  conversationId: string;
  senderType: SupportMessageSenderType;
  senderId: string | null;
  senderName: string | null;
  senderProfilePhoto: string | null;
  body: string;
  attachmentUrl?: string | null;
  attachmentMimeType?: string | null;
  channel: SupportMessageChannel;
  readAt: string | null;
  createdAt: string;
}

export interface SupportThread {
  conversation: SupportConversation;
  messages: SupportMessage[];
}

export type SupportTypingEvent = {
  type: "typing.changed";
  conversationId: string;
  senderType: "user" | "admin";
  senderId: string;
  isTyping: boolean;
  at: string;
};

export type SupportSocketEvent =
  | { type: "message.created"; conversationId: string; message: SupportMessage }
  | { type: "conversation.updated"; conversation: SupportConversation }
  | SupportTypingEvent;

export const supportApi = {
  async workflows() {
    const res = await apiClient.get<ApiResponse<SupportWorkflowPreset[]>>(
      "/admin/support/workflows",
    );
    return res.data.data;
  },

  async list(params: {
    page?: number;
    limit?: number;
    status?: SupportConversationStatus | "";
    assignedToMe?: boolean;
  }) {
    const query = {
      page: params.page,
      limit: params.limit,
      ...(params.status ? { status: params.status } : {}),
      ...(params.assignedToMe ? { assignedToMe: true } : {}),
    };
    const res = await apiClient.get<ApiResponse<PaginatedResponse<SupportConversation>>>(
      "/admin/support/conversations",
      { params: query },
    );
    return res.data.data;
  },

  async get(id: string) {
    const res = await apiClient.get<ApiResponse<SupportThread>>(
      `/admin/support/conversations/${id}`,
    );
    return res.data.data;
  },

  async sendMessage(conversationId: string, body: string) {
    const res = await apiClient.post<ApiResponse<SupportMessage>>(
      `/admin/support/conversations/${conversationId}/messages`,
      { body },
    );
    return res.data.data;
  },

  async refreshAiSuggestion(conversationId: string) {
    const res = await apiClient.post<ApiResponse<SupportThread>>(
      `/admin/support/conversations/${conversationId}/ai-suggestion`,
      {},
    );
    return res.data.data;
  },

  async assignToMe(conversationId: string) {
    const res = await apiClient.patch<ApiResponse<SupportThread>>(
      `/admin/support/conversations/${conversationId}/assign`,
      {},
    );
    return res.data.data;
  },

  async update(
    conversationId: string,
    payload: {
      status?: SupportConversationStatus;
      priority?: SupportConversationPriority;
    },
  ) {
    const res = await apiClient.patch<ApiResponse<SupportThread>>(
      `/admin/support/conversations/${conversationId}`,
      payload,
    );
    return res.data.data;
  },

  async updateWorkflow(
    conversationId: string,
    payload: {
      workflowType?: SupportWorkflowType;
      workflowStatus?: SupportWorkflowStatus;
      checklistItemId?: string;
      checked?: boolean;
      linkedEntityType?: string;
      linkedEntityId?: string;
      note?: string;
    },
  ) {
    const res = await apiClient.patch<ApiResponse<SupportThread>>(
      `/admin/support/conversations/${conversationId}/workflow`,
      payload,
    );
    return res.data.data;
  },
};

export function connectSupportSocket(
  onEvent: (event: SupportSocketEvent) => void,
) {
  const token = localStorage.getItem("admin_access_token");
  if (!token) return null;

  const ws = new WebSocket(`${SUPPORT_WS_URL}?token=${encodeURIComponent(token)}`);
  ws.onmessage = (event) => {
    try {
      onEvent(JSON.parse(event.data) as SupportSocketEvent);
    } catch {
      // Ignore malformed frames; REST refresh remains the source of truth.
    }
  };
  return ws;
}
